import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../components/Toast/Toast";

import { LoadingView } from "../../components/loading-view/LoadingView";
import { ErrorView } from "../../components/error-view/ErrorView";
import { usePageQuery } from "../../shared/hooks/usePageQuery";
import {
  PagedComponentState,
  usePagedComponentStateMachine,
} from "../../shared/hooks/usePageComponentStateMachine";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { useCustomerAndVendors } from "../../shared/hooks/useCustomerAndVendors";
import { OrderListWrapper } from "./wrappers/order-list.wrapper";
import { Order } from "../../../core/models/entities/Order";
import { useDialog } from "../../components/Dialogs/dialog-base";
import { RejectionNoteDialog } from "../../components/Dialogs/rejection-note-popup.component";
import { OrderTitleHeader } from "../../components/Logo/LogoTitleHeader";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { useCartHidden } from "../../../hooks/useCartHidden";
import { useWorkshopMode } from "../../../hooks/useWorkshopMode";
import {
  useCanAdjustOrderPrices,
  useCanPayOrders,
} from "../../components/Order/PayNow";
import { AdjustPricesDialog } from "./components/AdjustPricesDialog";
import { useConfirmationDialog } from "../../components/Dialogs/confirmation-dialog";
import { formatMoney } from "../admin-api-keys/shared/formatters";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCreditCard } from "@fortawesome/free-solid-svg-icons";
import { enviroment } from "../../../enviroment";
import { stripShowOnlyOwnedFromQuery } from "./functions/showOnlyOwnedFilter";
import { resolveOrderRowTarget } from "./functions/orderRowClick";

export function OrderListScreen(): React.ReactElement {
  const navigate = useNavigate();
  const [query, queryChanger] = usePageQuery(25);
  // Flag ordersOwnedFilterFixEnabled: a showOnlyOwned filter that still sits
  // in the URL/state (deep link, Back/Forward, a bookmark from before the
  // fix) is dropped before it reaches the fetch, the paginator prefetch and
  // the list header - the API has no such field and answers 500, and the
  // fetch fires on the first render, before the header could ever clean it
  // up. The same object as `query` when there is nothing to strip. Off =
  // `query` as today.
  const listQuery = enviroment.ordersOwnedFilterFixEnabled
    ? stripShowOnlyOwnedFromQuery(query)
    : query;
  const { state, dataTransition } = usePagedComponentStateMachine();
  const { orderRepository, organizationRepository } = useRepositoriesContext();
  const [lastPage, setLastPage] = useState<number>(1);
  const [orderRejection, setOrderRejection] = useState<Order | null>(null);
  const { profile, role } = useProfileContext();
  const rejectionNoteDialog = useDialog();
  const { t } = useTranslation();

  // Stripe Checkout return: the success/cancel URLs land back on the order
  // list with ?stripe=success|cancelled. Surface the outcome as a toast once
  // and strip the param (replace, so back/refresh can't re-trigger it). The
  // list itself already reflects the new status via its normal fetch.
  const [stripeParams, setStripeParams] = useSearchParams();
  useEffect(() => {
    const stripeResult = stripeParams.get("stripe");
    if (!stripeResult) return;
    if (stripeResult === "success") {
      showSuccessToast(t("orderPayment.successToast"));
    } else if (stripeResult === "cancelled") {
      showInfoToast(t("orderPayment.cancelledToast"));
    }
    const next = new URLSearchParams(stripeParams);
    next.delete("stripe");
    setStripeParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function showRejectionNote(order: Order): void {
    setOrderRejection(order);
    rejectionNoteDialog.openDialog();
  }

  // Admin cancel of an Awaiting payment row: per-row state + shared
  // confirmation dialog (same pattern as RejectionNoteDialog above). The
  // dialog text re-derives from state on every render, so the hook's static-
  // text contract is satisfied.
  const [orderToCancel, setOrderToCancel] = useState<Order | null>(null);
  const cancelDialog = useConfirmationDialog(
    t("orderPayment.cancelConfirm", {
      orderNumber: orderToCancel?.orderNumber ?? "",
    }),
    () => void confirmCancelOrder(),
    () => setOrderToCancel(null),
  );

  function requestCancelOrder(order: Order): void {
    setOrderToCancel(order);
    cancelDialog.open();
  }

  async function confirmCancelOrder(): Promise<void> {
    const order = orderToCancel;
    if (!order) return;
    setOrderToCancel(null);
    try {
      await orderRepository.cancelAwaitingOrder(order.id);
      // Covers both "cancelled" and the idempotent "alreadyCancelled" - the
      // row is gone from Awaiting payment either way. The repository already
      // invalidated the ["orders"] queries (list + banner + badge).
      showSuccessToast(
        t("orderPayment.cancelSuccess", { orderNumber: order.orderNumber }),
      );
    } catch {
      // 409 from the API = the order moved on (paid/committed) before the
      // claim - or plain transport failure. Either way: nothing was changed
      // server-side beyond what really happened; the refetched list shows it.
      showErrorToast(
        t("orderPayment.cancelError", { orderNumber: order.orderNumber }),
      );
    }
  }

  // Admin price adjustment (flag REACT_APP_ADMIN_PRICE_ADJUSTMENT_ENABLED):
  // per-row state + the dialog, mounted only while the gate is open so the
  // flag-off tree is identical to before.
  const canAdjustPrices = useCanAdjustOrderPrices();
  const [orderToAdjust, setOrderToAdjust] = useState<Order | null>(null);
  const adjustPricesDialog = useDialog();

  function requestAdjustPrices(order: Order): void {
    setOrderToAdjust(order);
    adjustPricesDialog.openDialog();
  }

  const {
    data: ordersPage,
    isSuccess: isOrdersSuccess,
    isLoading: isOrdersLoading,
    isError: isOrdersError,
    isFetching: isOrdersFetching,
    isPreviousData: isOrdersPreviousData,
  } = orderRepository.useAllOrders(listQuery, true, { keepPreviousData: true });

  const {
    customers,
    vendors,
    isSuccess: isOrganizationSuccess,
    isError: isOrganizationError,
    isLoading: isOrganizationLoading,
  } = useCustomerAndVendors(organizationRepository);

  // Stripe prepayment: outstanding total for the roles that can actually pay
  // (Vendor/Admin — end customers never meet Stripe). Renders as a chip above
  // the list only when something is parked awaiting payment.
  const canPay = useCanPayOrders();
  const { data: awaitingSummary } =
    orderRepository.useAwaitingPaymentSummary(canPay);
  const awaitingAmounts = (awaitingSummary?.byCurrency ?? [])
    .map((c) => formatMoney(c.total, c.currency))
    .join(" + ");

  if (ordersPage && ordersPage.pageCount !== lastPage) {
    setLastPage(ordersPage.pageCount);
  }

  // keepPreviousData: while a new search/filter/page is loading, the paginator
  // still shows the previous result's page range, so the user can click a page
  // beyond the new result's last page. Once fresh data lands, clamp back to
  // the (new) last page (same pattern as the logo list). No-op when the index
  // is already in range.
  useEffect(() => {
    if (!ordersPage || isOrdersPreviousData) return;
    // An empty result (pageCount 0) would otherwise leave the index stranded
    // (e.g. "page 7 of 0") - park it on page 1.
    const lastValidPage = Math.max(ordersPage.pageCount, 1);
    if (query.page.index > lastValidPage) {
      queryChanger.handleNewPage({ value: String(lastValidPage) });
    }
  }, [ordersPage, isOrdersPreviousData, query.page.index, queryChanger]);

  // Hover-prefetch for the paginator: warm the hovered page with exactly the
  // request a click would issue (prefetchOrders shares the click's cache key,
  // so this never changes what a click shows). Cheap: GetOrders pages are
  // light; GetOrder details are never prefetched from here.
  function handlePrefetchPage(pageIndex: number) {
    // While previous data is shown (keepPreviousData) the paginator's page
    // range belongs to the OLD query - hovering it must not fire calls for
    // potentially out-of-range pages of the new one.
    if (isOrdersPreviousData) return Promise.resolve();
    return orderRepository.prefetchOrders({
      ...listQuery,
      page: { ...listQuery.page, index: pageIndex },
    });
  }

  // Hidden-price customers keep the list, but row-click leads to order/confirm
  // (prices) - make it a no-op instead of bouncing through the route guard.
  // Workshop users go to the price-stripped details view (their work sheet).
  // Dead clicks round 2 (flag clarityUx2Enabled): the no-op becomes a toast
  // that says why (resolveOrderRowTarget) - no navigation, no call.
  const cartHidden = useCartHidden();
  const workshopMode = useWorkshopMode();
  const navigateOrder = (order: Order) => {
    const target = resolveOrderRowTarget(order, {
      workshopMode,
      cartHidden,
      ux2: enviroment.clarityUx2Enabled,
    });
    if (target.kind === "navigate") navigate(target.to);
    else if (target.kind === "toast") showInfoToast(t("ux2.orderPricesHidden"));
  };

  const isLoading = isOrdersLoading || isOrganizationLoading;
  const isSuccess = isOrdersSuccess && isOrganizationSuccess;
  const isError = isOrdersError || isOrganizationError;
  dataTransition(isLoading, isSuccess, isError);

  return state === PagedComponentState.Succeed ||
    state === PagedComponentState.PageLoading ? (
    <>
      {canPay && (awaitingSummary?.count ?? 0) > 0 && (
        <div
          className={
            "mx-8 mt-4 flex items-center gap-2 rounded border border-orange-300 " +
            "bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700"
          }
          data-testid="awaiting-payment-summary"
        >
          <FontAwesomeIcon icon={faCreditCard} />
          <span>
            {t("orderPayment.outstandingTotal", {
              count: awaitingSummary!.count,
              amounts: awaitingAmounts,
            })}
          </span>
        </div>
      )}
      <OrderListWrapper
        profile={profile!}
        query={listQuery}
        queryChanger={queryChanger}
        isPageLoading={state === PagedComponentState.PageLoading}
        isFetching={isOrdersFetching}
        isPreviousData={isOrdersPreviousData}
        onPrefetchPage={handlePrefetchPage}
        lastPage={lastPage}
        orders={ordersPage?.results}
        customers={customers!}
        vendors={vendors}
        onSelectOrder={navigateOrder}
        onRejectionNote={showRejectionNote}
        onCancelOrder={requestCancelOrder}
        onAdjustPrices={canAdjustPrices ? requestAdjustPrices : undefined}
      />
      {cancelDialog.component}
      {canAdjustPrices && (
        <AdjustPricesDialog
          dialogVm={adjustPricesDialog}
          order={orderToAdjust}
          onClose={() => setOrderToAdjust(null)}
        />
      )}
      {orderRejection && (
        <RejectionNoteDialog
          title={<OrderTitleHeader order={orderRejection!} />}
          dialogOptions={rejectionNoteDialog}
          approval={orderRejection.approval!}
          exclude={role!}
          isOrder={true} // This is for an order, so hide edit button
        />
      )}
    </>
  ) : state === PagedComponentState.Failed ? (
    <ErrorView />
  ) : (
    <LoadingView />
  );
}
